<template>
  <div>
    <ExitNavBar :title="navTitle" @exit="onExit" />
    <OrderEditTimeoutInfo
      v-if="showEditOrderTimeoutInfo && orderStore.activeOrder"
      @timeout-reached="onEditOrderTimeoutReached"
      :order-created-at="new Date(orderStore.activeOrder.date)" />
    <ButtonDisabledInfoStripe
      v-if="!loading && warningMessage"
      :warning-message="warningMessage"
      :account-receivables-email="accountReceivablesEmail" />
    <div class="add-edit-order">
      <div class="navbar-background" id="addEditNavBar">
        <div class="add-edit-order-nav-bar" data-cy="orders-nav-bar">
          <div class="requested-date" data-cy="requested-date">
            <div v-if="showRequestedDateSelector">
              <BackstageInputCalendar
                :model-value="requestedDeliveryDate"
                name="requested-date"
                :label="$t('matrix.requested_date')"
                :disabled="!isDateChangeEnabled"
                :min-date="seasonStartDate"
                :max-date="seasonEndDate"
                :placeholder="$t(`matrix.ship_date_placeholder`)"
                v-on:update:modelValue="onOrderDateChange" />
            </div>
          </div>
          <BreadcrumbMenu class="breadcrumb-menu" :crumbs="breadcrumbItems" />
          <div class="action-buttons">
            <BackstageButton
              :disabled="!isCurrentStepActionEnabled"
              :icon-right="submitting ? 'loading' : 'arrow-right'"
              :data-cy="`${currentStep}-button-cnt`"
              :data-cy-button="`${currentStep}-button`"
              @click.prevent="handleStepAction">
              {{ currentStepActionButtonLabel }}
            </BackstageButton>
          </div>
        </div>
      </div>

      <div
        v-if="(isMultipleOrder || isSpecialProjects) && currentOrderStepIsQuantities"
        class="dates-selector-cnt"
        id="matrixRelatedOrdersSelector">
        <MasterOrderSelector
          :orders="multipleOrdersSelectorList"
          :enable-add-button="isOrderSelectorActionsEnabled"
          :enable-edit-button="isOrderSelectorActionsEnabled"
          :enable-clone-button="isOrderSelectorActionsEnabled"
          :is-at-max-orders="isAtMaxOrders"
          :enable-delete-button="isOrderSelectorActionsEnabled && multipleOrdersSelectorList.length > 1"
          :selected-order-is-valid="isOrderDetailsValid"
          @set-selector-ref="setMatrixSelectorRef"
          @edit-order="toggleDatePicker"
          @clone-order="onCloneOrder"
          @add-new-order="multipleSelectorNewMatrixOrder"
          @delete-order="onDeleteOrder"
          @select-order="onSelectOrder" />
        <div
          v-if="showDatePicker"
          ref="datePickerTarget"
          class="products-quantities-order-date-cnt"
          :style="{ left: datepickerLeftPosition }">
          <InputValidator>
            <VueDatepicker
              :id="`order-${productMatrixStore.activeMatrixOrder?.orderNumber}-date`"
              :format="formatDateNoUTC"
              :start-date="seasonStartDate"
              :min-date="seasonStartDate"
              :max-date="seasonEndDate"
              :locale="userLocale"
              :placeholder="$t(`matrix.ship_date_placeholder`)"
              :inline="true"
              :enable-time-picker="false"
              :month-change-on-scroll="false"
              :auto-apply="true"
              @update:model-value="onOrderDateChange" />
          </InputValidator>
        </div>
      </div>

      <div v-if="loading" class="loader"></div>

      <div v-else class="current-step-content" :class="[{ 'pop-up-open': showSubmitReminderPopup }]">
        <!-- Products Models Selector -->
        <ProductsModelsSelectorV3
          v-if="currentOrderStepIsProductModelsSelection"
          :order-type="orderStore.orderType as OrderType"
          :order-date="requestedDeliveryDate"
          :order-number="productMatrixOrderNumber" />

        <ProductsConfirmationModal v-if="showProductsConfirmationModal" @proceed="onProceedToOrderDetails">
          <BackstageButton
            data-cy-button="go-back"
            @click="showProductsConfirmationModal = false"
            :variant="ButtonVariant.SECONDARY">
            <BackstageIcon name="arrow-left" />
            <span class="spacer"> {{ $t(`viewOrder.confirmation_modal.go_back`) }}</span>
          </BackstageButton>
          <BackstageButton
            data-cy-button="proceed"
            @click="onProceedToOrderDetails(false)"
            :variant="ButtonVariant.PRIMARY">
            <span>{{ $t(`viewOrder.confirmation_modal.proceed`) }}</span>
          </BackstageButton>
        </ProductsConfirmationModal>

        <ProductsQuantitiesSelectorV3
          v-if="currentOrderStepIsQuantities"
          :isExistingOrder="isOrderEditMode"
          :seasonStart="seasonStartDate"
          :overall-total="isMultipleOrder ? matrixStats.overallTotal : undefined"
          :is-quantity-increase-allowed="!isOutsideInitialSellIn"
          :order-number="productMatrixOrderNumber" />

        <!-- Order Details -->
        <div v-if="currentOrderStepIsOrderDetails" class="common-container">
          <ViewEditOrders3
            :key="requestedDeliveryDate + 'order-details'"
            :id="productMatrixStore.activeMatrixOrder?.orderId"
            :is-draft="!isOrderEditMode"
            :edit-type="props.editType"
            @update-order="updateOrdersFromViewEditOrders"
            @orders-are-valid="setOrderDetailsValidity"
            @add-new-order="multipleSelectorNewMatrixOrder" />
        </div>

        <NotificationMessage
          v-if="showSubmitReminderPopup"
          :title="$t('viewOrder.submit_reminder_popup_title')"
          :size="'small'"
          show-close-button
          v-on:close="showSubmitReminderPopup = false">
          <SubmitReminder
            :submitting="submitting"
            :saving="saving"
            :deleting="deleting"
            :disabled-actions="isOrderEditMode ? ['delete', 'save'] : []"
            :submit-enabled="isSubmitEnabled"
            :save-draft-enabled="isSaveDraftEnabled"
            @save="onSaveDraft"
            @delete="deleteAllDraftsOnExit"
            @submit="onSubmitOrders" />
        </NotificationMessage>

        <SalesRepContactPopup
          v-if="showContactsPopup"
          :userContacts="userContacts"
          @contactUpdated="showContactsPopup = false"
          :editType="props.editType" />

        <NotificationMessage v-if="processingMessage" v-bind="notificationBaseProps" :size="'small'">
          <div class="processing-content">
            <BackstageIcon name="loading" class="processing-icon" />
            <p>{{ processingMessage }}</p>
          </div>
        </NotificationMessage>

        <NotificationMessage v-if="partialSubmitResult" v-bind="notificationBaseProps" :size="'medium'">
          <div class="partial-submit-content">
            <div class="partial-submit-scrollable">
              <div class="partial-submit-header">
                <BackstageIcon name="info-circle" class="partial-submit-icon" />
                <h3>{{ $t('global.partial_submit_title') }}</h3>
              </div>
              <p class="partial-submit-description">{{ $t('global.partial_submit_description') }}</p>
              <div class="partial-submit-section">
                <h4>{{ $t('global.partial_submit_succeeded_section') }} ({{ succeededCount }})</h4>
                <ul>
                  <li v-for="orderNumber in partialSubmitResult.succeededOrderNumbers" :key="orderNumber">
                    {{ orderNumber }}
                  </li>
                </ul>
              </div>
              <div class="partial-submit-section">
                <h4>{{ $t('global.partial_submit_failed_section') }} ({{ failedCount }})</h4>
                <ul>
                  <li v-for="item in partialSubmitResult.failedItems" :key="item.draftNumber">
                    {{ item.draftNumber }} - {{ item.errors.join(', ') }}
                  </li>
                </ul>
              </div>
            </div>
            <div class="partial-submit-button">
              <BackstageButton :full-width="true" @click="goToOrders">
                {{ $t('global.partial_submit_go_to_orders') }}
              </BackstageButton>
            </div>
          </div>
        </NotificationMessage>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  AmtSeasonDto,
  ContactModel,
  MAX_BULK_DRAFT_ORDERS,
  OrderType,
  OrderTypeDto,
  isSeasonalOrderType,
} from '@onrunning/backstage-models';
import VueDatepicker from '@vuepic/vue-datepicker';
import { onClickOutside } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageInputCalendar from '@/components/common/BackstageInputCalendar.vue';
import BreadcrumbMenu from '@/components/common/breadcrumb-menu.vue';
import ButtonDisabledInfoStripe from '@/components/common/ButtonDisabledInfoStripe.vue';
import ExitNavBar from '@/components/common/exit-nav-bar.vue';
import InputValidator from '@/components/common/input-validator.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import SubmitReminder from '@/components/common/submit-reminder.vue';
import ProductsConfirmationModal from '@/components/matrix/products-confirmation-modalV3.vue';
import ProductsModelsSelectorV3 from '@/components/matrix/products-models-selector-v2.vue';
import ProductsQuantitiesSelectorV3 from '@/components/matrix/products-quantities-selector-v3.vue';
import MasterOrderSelector from '@/components/orders/master-order-selector.vue';
import OrderEditTimeoutInfo from '@/components/orders/order-edit-timeout-info.vue';
import SalesRepContactPopup from '@/components/orders/SalesRepContactPopup.vue';
import ViewEditOrders3 from '@/components/orders/view-edit-ordersV3.vue';
import { useAnalytics } from '@/composables/analytics';
import { useFormat } from '@/composables/format';
import { useLocale } from '@/composables/locale';
import { useBreadcrumbMenu } from '@/composables/useBreadcrumbMenu';
import { useFetchAccount } from '@/composables/useFetchAccount';
import { useHelper } from '@/composables/useHelpers';
import { useNotification } from '@/composables/useNotification';
import { useOrderManagement } from '@/composables/useOrderManagement';
import { useOrderValidation } from '@/composables/useOrderValidation';
import { addEditOrderStepEvent } from '@/constants/ga-custom-events.constants';
import { OrderEditType } from '@/constants/orderEditType';
import { localeMixins } from '@/mixins/locale.mixins';
import { ordersMixins } from '@/mixins/orders.mixins';
import { AddEditOrderBreadcrumbSteps } from '@/models/add-edit-order-breadcrumb-steps.enum';
import { ButtonVariant } from '@/models/button-variant.enum';
import type { MasterOrderSelectorModel } from '@/models/master-order-selector.model';
import { RouteNames } from '@/router';
import { useAccountStore } from '@/stores/account';
import { useOrderStore } from '@/stores/order';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { useUserStore } from '@/stores/user';
import type { Draft } from '@/types/draft.interface';
import type { MatrixOrder } from '@/types/matrix.interface';
import type { EditableOrder } from '@/types/order.interface';
import type { MatrixOrderProduct } from '@/types/product.interface';
import { toLocalMidnightDate } from '@/utils/temporalUtils';
import { useDraftsInitInfoStore } from '../stores/drafts-init-info';

const props = defineProps<{
  id?: string;
  editType?: OrderEditType;
  contactId?: string;
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const notificationBaseProps = {
  showCloseButton: false,
  showOutsideCloseButton: false,
};

const succeededCount = computed(() => partialSubmitResult.value?.succeededOrderNumbers.length ?? 0);
const failedCount = computed(() => partialSubmitResult.value?.failedItems.length ?? 0);

const userStore = useUserStore();
const orderStore = useOrderStore();
const accountStore = useAccountStore();
const { userLocale } = useLocale();
const { addCustomGaEvent } = useAnalytics();
const { isReOrderAllowedForONBR } = useHelper();
const productMatrixStore = useProductMatrixStore();

const { fetchAccountDetails } = useFetchAccount();

const { notifyError } = useNotification();
const {
  formatDateNoUTC,
  formatNonLocalisedDateString,
  formatDateToLocaleStringIgnoringTime,
  formatDateToIsoStringIgnoringTime,
} = useFormat();
const {
  submitting,
  saving,
  deleting,
  partialSubmitResult,
  processingMessage,
  onSaveDraft,
  deleteDraft,
  deleteAllDraftsOnExit,
  onSubmitOrders,
  onProceedToOrderDetails,
  seasonStartDate,
  seasonEndDate,
  getOrders,
  showProductsConfirmationModal,
} = useOrderManagement({ editType: props.editType, contactId: toRef(props, 'contactId') });
const {
  matrixStats,
  canDealerEditOrder,
  isSaveDraftEnabled,
  isSubmitEnabled,
  isOrderDetailsValid,
  isDraftEditMode,
  isOrderEditMode,
  isNewDraft,
  isMultipleOrder,
  isTimeoutForOrderEditing,
  isDealerInReadOnlyMode,
  isOutsideInitialSellIn,
  isSpecialProjects,
} = useOrderValidation(props.editType);

const loading = ref(true);
const showDatePicker = ref(false);
const datepickerLeftPosition = ref('0px');
const showSubmitReminderPopup = ref(false);
const matrixSelectorRefs = ref<HTMLElement[]>([]);
const datePickerTarget = ref<HTMLElement | null>(null);
const routeOrderType = ref<OrderType | undefined>(
  ordersMixins.mapOrderTypeByOrderType(route.params.orderType as string | undefined),
);
const { getInitialInfoData } = useDraftsInitInfoStore();

const accountReceivablesEmail = computed(() => {
  if (accountStore.isOnHold) {
    return accountStore.accountReceivablesEmail;
  }

  return undefined;
});

const showEditOrderTimeoutInfo = computed(() => {
  return isOrderEditMode.value && canDealerEditOrder.value;
});

const requestedDeliveryDate = computed((): string => {
  return formatNonLocalisedDateString(productMatrixStore.activeMatrixOrder?.date as Date) ?? '';
});

const productMatrixOrderNumber = computed((): string | undefined => {
  return isOrderEditMode.value && isOutsideInitialSellIn.value ? orderStore.activeOrder?.orderNumber : undefined;
});

const isReorderDisabled = computed((): boolean => {
  return orderStore.orderType === OrderType.ReOrder && !isReOrderAllowedForONBR();
});

const warningMessage = computed((): string | null => {
  if (accountStore.isOnHold) {
    return t('viewOrder.submit_button_disabled_acc_onhold');
  }
  if (!matrixStats.value.isRequestedReceiptDateValid) {
    return t('viewOrder.submit_button_disabled_rrd');
  }
  if (isReorderDisabled.value) {
    return t('viewOrder.reorder_warning_message');
  }

  return null;
});

const isOrderSelectorActionsEnabled = computed((): boolean => {
  return !isOrderEditMode.value && !currentOrderStepIsOrderDetails.value;
});

const isDateChangeEnabled = computed((): boolean => {
  return !isOrderEditMode.value && !currentOrderStepIsOrderDetails.value;
});

const navTitle = computed(() => {
  if (!orderStore.orderType) return '';

  const title = localeMixins.getLocaleTextByOrderType('viewOrders.order_type.', orderStore.orderType);
  let navTitle = t(title);

  if (orderStore.orderType !== OrderType.MultiplePreOrder && orderStore.activeOrder?.orderNumber) {
    navTitle += ` - ${orderStore.activeOrder.orderNumber}`;
  }

  return navTitle;
});

const isCurrentStepActionEnabled = computed((): boolean => {
  if (submitting.value || isDealerInReadOnlyMode.value || isReorderDisabled.value) {
    return false;
  }

  switch (currentStep.value) {
    case AddEditOrderBreadcrumbSteps.productsModelsSelection:
      return productMatrixStore.selectedProductModelsNames.size > 0;
    case AddEditOrderBreadcrumbSteps.productsQuantities:
      return matrixStats.value.allQuantitiesValid;
    case AddEditOrderBreadcrumbSteps.orderDetails:
      return isSubmitEnabled.value;
    default:
      return true;
  }
});

const currentStepActionButtonLabel = computed((): string => {
  if (currentStep.value === AddEditOrderBreadcrumbSteps.orderDetails) {
    return accountStore.isOnHold
      ? t(`create_edit_order.execute_save_draft_button`)
      : t(`create_edit_order.execute_${currentStep.value}_step_button`);
  } else {
    return t(`create_edit_order.execute_${currentStep.value}_step_button`);
  }
});

const currentOrderStepIsProductModelsSelection = computed(
  () => currentStep.value === AddEditOrderBreadcrumbSteps.productsModelsSelection,
);
const currentOrderStepIsQuantities = computed(
  () => currentStep.value === AddEditOrderBreadcrumbSteps.productsQuantities,
);
const currentOrderStepIsOrderDetails = computed(() => currentStep.value === AddEditOrderBreadcrumbSteps.orderDetails);

// Special Projects uses the same multi-date MasterOrderSelector as MultiplePreOrder,
// so the single requested-date selector is hidden for both order types.
const showRequestedDateSelector = computed((): boolean => {
  return !isMultipleOrder.value && !isSpecialProjects.value;
});

const multipleOrdersSelectorList = computed((): MasterOrderSelectorModel[] => {
  return productMatrixStore.matrixOrders.map((order, index) => ({
    id: order.orderNumber ?? '',
    title: order.date ? formatDateToLocaleStringIgnoringTime(order.date) : t('matrix.ship_date_placeholder').toString(),
    subTitle: `${order.total.toString()} ${t('viewOrder.items')}`,
    selected: index === productMatrixStore.activeMatrixOrderIndex,
  }));
});

const isAtMaxOrders = computed((): boolean => productMatrixStore.matrixOrders.length >= MAX_BULK_DRAFT_ORDERS);

const breadCrumbConfig = computed(() => {
  const breadcrumbDate = formatNonLocalisedDateString(productMatrixStore.matrixOrders[0]?.date as Date);

  return {
    steps: Object.values(AddEditOrderBreadcrumbSteps),
    translationNamespace: 'create_edit_order',
    routePrefix: 'orders',
    extraPath: props.id
      ? `${props.id}/edit-products/${props.editType}`
      : `create/${orderStore.orderType?.toLocaleLowerCase()}/${breadcrumbDate}/${props.contactId ?? ''}`,
  };
});

const { breadcrumbItems, currentStep } = useBreadcrumbMenu<AddEditOrderBreadcrumbSteps>(breadCrumbConfig);

watch(
  () => currentStep.value,
  (step) => {
    const gaData = addEditOrderStepEvent[step];
    if (gaData) {
      addCustomGaEvent(gaData);
    }
  },
  { immediate: true },
);

watch(
  () => productMatrixStore.activeMatrixOrder,
  (activeMatrixOrder) => {
    orderStore.setActiveOrderNumber(activeMatrixOrder?.orderNumber || null);
  },
);

watch(
  () => productMatrixStore.matrixOrders[0]?.date,
  (newDate, oldDate) => {
    if (newDate !== oldDate && isNewDraft.value) {
      updateUrlDate(newDate);
    }
  },
);

const userContacts = ref<ContactModel[]>([]);
const showContactsPopup = ref(false);
const checkSalesRepContact = (): boolean => {
  // If sales rep creating a new draft order, contactId is required
  if (userStore.isASalesRep) {
    if (isNewDraft.value && !props.contactId) {
      showContactsPopup.value = true;

      return false;
    }

    // if editing an existing order, contactId is required in all created drafts
    if (isDraftEditMode.value) {
      const noContactIdDrafts = Array.from(orderStore.orders.values()).filter((order) => !order.contactId);
      if (noContactIdDrafts.length > 0) {
        showContactsPopup.value = true;

        return false;
      }
    }
  }

  return true;
};

onMounted(async () => {
  window.addEventListener('beforeunload', onBeforeUnloadHandler);

  if (isOrderEditMode.value && isTimeoutForOrderEditing.value) {
    router.replace({
      name: RouteNames.ViewOrder,
      params: {
        id: props.id,
      },
    });
  }

  await init();

  if (currentStep.value !== AddEditOrderBreadcrumbSteps.orderDetails) {
    // need to call this after draftsService.getInitInfo called
    checkSalesRepContact();
  }
});

onUnmounted(() => {
  window.removeEventListener('beforeunload', onBeforeUnloadHandler);
  orderStore.reset();
  productMatrixStore.reset();
});

const init = async () => {
  loading.value = true;
  try {
    await Promise.all([fetchAccountDetails(userStore.selectedAccount.id), initOrders()]);
  } catch (error: any) {
    notifyError(error?.message || t('backend.orders.fetch_order_error'));
    router.push('/orders');
  } finally {
    loading.value = false;
  }
};

const initOrders = async () => {
  let orderType: OrderType;
  let seasonData: string[] | AmtSeasonDto[] | undefined;
  if (props.id) {
    const allOrders = await getOrders(props.id, isDraftEditMode.value);
    orderType = allOrders[0].type;
    seasonData = allOrders[0].seasons?.[0] ? allOrders[0].seasons : undefined;

    initStore(allOrders as Draft[] | EditableOrder[]);
  } else {
    productMatrixStore.initMatrixOrder(toLocalMidnightDate(route.params['startDate'] as string));
    orderType = routeOrderType.value as OrderType;
  }

  await initializeOrderTypeInfo(orderType, seasonData);
};

const initStore = (orders: Draft[] | EditableOrder[]) => {
  const matrixOrders: MatrixOrder[] = [];
  const orderMap = new Map<string, Draft | EditableOrder>();

  for (const order of orders) {
    orderMap.set(order.orderNumber, order);

    let matrixTotal = 0;
    const matrixProducts: Record<string, MatrixOrderProduct> = {};
    const productGroupNames = new Set<string>();
    const itemNumbers = new Set<string>();

    for (const product of order.products) {
      const matrixProduct: MatrixOrderProduct = {
        productName: product.description,
        productGroupId: product.name || product.productGroupName,
        productGroupName: product.productGroupName,
        productGroupNumber: product.name,
        quantity: product.quantity,
        productId: product.productId,
      };

      matrixProducts[matrixProduct.productId] = matrixProduct;
      matrixTotal += matrixProduct.quantity;

      if (product.name) {
        itemNumbers.add(product.name);
      }

      if (product.productGroupName) {
        productGroupNames.add(product.productGroupName);
      }
    }

    const matrixOrder: MatrixOrder = {
      orderId: order.id,
      orderNumber: order.orderNumber,
      date: toLocalMidnightDate(order.dueDate),
      matrixProducts,
      total: matrixTotal,
    };

    matrixOrders.push(matrixOrder);
    productMatrixStore.updateSelectedProductModel(productGroupNames);
    productMatrixStore.updateSelectedItemNumbers(itemNumbers);
  }

  orderStore.setOrders(orderMap);
  productMatrixStore.setMatrixOrders(matrixOrders);
};

const initializeOrderTypeInfo = async (
  orderType: OrderType,
  seasonDataArray?: string[] | AmtSeasonDto[],
): Promise<void> => {
  let orderTypeInitInfo: OrderTypeDto;
  let orderTypes: OrderTypeDto[];

  const initInfo = await getInitialInfoData(userStore.selectedAccount.id);
  userContacts.value = initInfo?.userContacts ?? [];
  orderTypes = initInfo?.orderTypes ?? [];

  if (seasonDataArray && isOpenPreOrder(orderType)) {
    const seasonData = seasonDataArray?.[0] as AmtSeasonDto;
    orderTypeInitInfo = {
      type: orderType,
      startDate: seasonData.startDate,
      defaultDate: seasonData.startDate,
      seasonName: seasonData.name,
      endDate: seasonData.endDate,
    };
  } else {
    // fallback to reorder when its dropship
    const type = orderType === OrderType.DropShip ? OrderType.ReOrder : orderType;
    orderTypeInitInfo = orderTypes.find((orderType) => orderType.type === type) as OrderTypeDto;
  }

  if (orderTypeInitInfo) {
    orderStore.setOrderTypeInfo(orderTypeInitInfo, orderType);
  }
};

const handleStepAction = () => {
  switch (currentStep.value) {
    case AddEditOrderBreadcrumbSteps.productsModelsSelection:
      if (!checkSalesRepContact()) {
        return;
      }
      router.push({
        params: { ...route.params, step: AddEditOrderBreadcrumbSteps.productsQuantities },
      });

      return;
    case AddEditOrderBreadcrumbSteps.productsQuantities:
      if (!checkSalesRepContact()) {
        return;
      }
      showProductsConfirmationModal.value = true;

      return;
    case AddEditOrderBreadcrumbSteps.orderDetails:
      onSubmitOrders();

      return;
  }
};

const onEditOrderTimeoutReached = () => {
  isTimeoutForOrderEditing.value = true;
  redirectUserToLastStep();
};

const redirectUserToLastStep = () => {
  router.push({
    name: RouteNames.EditOrder,
    params: {
      id: orderStore.activeOrder?.orderNumber,
      orderType: orderStore.activeOrder?.type,
      step: AddEditOrderBreadcrumbSteps.orderDetails,
    },
    force: true,
  });
};

const confirmNavigatingAway = (path: string) => {
  notifyError(t('global.warning_unsaved_changes'), {
    buttons: [
      {
        text: t('global.continue_button'),
        action: () => {
          router.push(path);
        },
        className: ButtonVariant.SECONDARY,
      },
    ],
  });
};

const onBeforeUnloadHandler = (event: BeforeUnloadEvent) => {
  if (productMatrixStore.matrixUpdated || orderStore.orderUpdated) {
    event.preventDefault();
  }
};

const goToOrders = () => {
  partialSubmitResult.value = null;
  router.push('/orders');
};

const onExit = () => {
  if (isSubmitEnabled.value) {
    showSubmitReminderPopup.value = true;

    return;
  }

  if (productMatrixStore.matrixUpdated || orderStore.orderUpdated) {
    confirmNavigatingAway('/orders');

    return;
  }

  router.push('/orders');
};

const updateUrlDate = (newDate: Date) => {
  router.replace({
    params: {
      ...route.params,
      startDate: formatDateToIsoStringIgnoringTime(newDate),
    },
  });
};

const onOrderDateChange = (date: Date) => {
  productMatrixStore.updateMatrixOrder(
    {
      date,
    },
    productMatrixStore.activeMatrixOrderIndex,
  );
};

const setMatrixSelectorRef = (el: HTMLElement, index: number) => {
  if (el) matrixSelectorRefs.value[index] = el;
};

const toggleDatePicker = (index: number) => {
  showDatePicker.value = !showDatePicker.value;

  const el = matrixSelectorRefs.value[index];

  if (el && showDatePicker.value) {
    const rect = el.getBoundingClientRect();
    datepickerLeftPosition.value = `${rect.left}px`;
  }
};

const multipleSelectorNewMatrixOrder = () => {
  if (isAtMaxOrders.value) {
    return;
  }

  const matrixOrder: MatrixOrder = {
    date: toLocalMidnightDate(seasonStartDate.value),
    matrixProducts: {},
    total: 0,
  };

  productMatrixStore.setMatrixOrder(matrixOrder);
};

const onSelectOrder = (index: number) => {
  productMatrixStore.setActiveMatrixOrderIndex(index);
};

const onDeleteOrder = async (index: number) => {
  await deleteDraft(index);

  const isDeletingFirstOrder = index === 0;

  if (isDraftEditMode.value && isDeletingFirstOrder) {
    const orderId = productMatrixStore.matrixOrders[0]?.orderId;

    if (orderId) {
      router.replace({
        params: {
          ...route.params,
          id: orderId,
        },
      });
    } else {
      const startDate = formatDateToIsoStringIgnoringTime(productMatrixStore.matrixOrders[0]?.date);
      router.replace({
        name: props.contactId ? RouteNames.CreateOrderForSalesRep : RouteNames.CreateOrderForDealer,
        params: {
          orderType: orderStore.orderType?.toLocaleLowerCase(),
          startDate,
          contactId: props.contactId ?? '',
          step: currentStep.value,
        },
      });
    }
  }
};

const onCloneOrder = (index: number) => {
  if (isAtMaxOrders.value) {
    return;
  }

  productMatrixStore.cloneMatrixOrder(index);
};

const setOrderDetailsValidity = (valid: boolean) => {
  isOrderDetailsValid.value = valid;
};

const updateOrdersFromViewEditOrders = (order: EditableOrder | Draft) => {
  orderStore.setUpdatedOrder(order);
};

const isOpenPreOrder = (orderType: OrderType): boolean => {
  return isOrderEditMode.value && isSeasonalOrderType(orderType);
};

onClickOutside(datePickerTarget, () => {
  showDatePicker.value = false;
});
</script>

<style lang="scss" scoped>
.add-edit-order {
  width: 100%;
  margin-bottom: 20px;

  .navbar-background {
    @include navbar-background;
    z-index: 5; // this is to keep the calendar on top of the next possible sticky bars
  }

  .add-edit-order-nav-bar {
    @include common-container;
    @include container-background;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 30px 20px;
    border-radius: 0;
    margin: 0px auto;

    .requested-date:deep() {
      width: 160px;
      margin: -8px 0;

      @include breakpoint(small) {
        min-height: 58px;
      }

      label {
        text-align: left;
        display: block;
        font-size: 0.75em;
        padding-bottom: 5px;
        cursor: pointer;
        color: var(--primary-text-remark-color);
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;

        &:hover {
          overflow: visible;
          white-space: normal;
          display: inline-flex;
        }
      }

      .vdp-datepicker input {
        width: 100px;
      }

      .dp__arrow_top {
        left: 82px;
      }
    }

    .breadcrumb-menu {
      flex-grow: 1;
    }

    .action-buttons {
      min-width: 160px;
      align-self: center;

      .button {
        white-space: nowrap;
      }
    }
  }

  .dates-selector-cnt {
    background-color: var(--primary-bg-color);
    border-top: 1px solid var(--bg-page-color);
    position: sticky;
    z-index: 4;
    top: 83px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    @include breakpoint(small) {
      padding: 0;
    }

    @include breakpoint(medium) {
      top: 63px;
    }

    @include breakpoint(small) {
      top: 173px;
    }

    .products-quantities-order-date-cnt {
      position: absolute;
      top: 100%;
      z-index: 3;
      left: 0px;
    }
  }

  .current-step-content {
    &.pop-up-open {
      position: inherit;
    }
  }

  .current-step-content:deep() {
    .common-container {
      @include common-container;
      padding: 8px 20px;
    }
  }

  .spacer {
    padding-left: 2.5px;
    padding-right: 2.5px;
  }
}

.spacer {
  padding-left: 2.5px;
  padding-right: 2.5px;
}

.processing-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px 0 8px;

  .processing-icon {
    width: 48px;
    height: 48px;
  }

  p {
    margin: 0;
    color: var(--primary-text-color);
    text-align: center;
  }
}

.partial-submit-content {
  padding: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  .partial-submit-scrollable {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-bottom: 8px;
  }

  .partial-submit-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;

    h3 {
      margin: 0;
      padding: 0;
      border-bottom: none;
      font-size: 33px;
      font-weight: 700;
      line-height: 1.1;
      text-align: left;
      color: var(--primary-text-color, black);
    }

    .partial-submit-icon {
      flex-shrink: 0;
      width: 32px;
      height: 32px;
    }
  }

  .partial-submit-description {
    margin: 0 0 12px;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    text-align: left;
    color: var(--primary-text-color, black);
  }

  .partial-submit-section {
    margin-bottom: 12px;

    h4 {
      font-size: 16px;
      font-weight: 700;
      line-height: 1.1;
      margin-bottom: 4px;
      padding: 4px 0;
      border: none;
      text-transform: none;
      letter-spacing: 0;
      text-align: left;
      color: var(--primary-text-color, black);
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        padding: 1px 0;
        color: #666;
        font-size: 16px;
        line-height: 1.5;
        text-align: left;
      }
    }
  }

  .partial-submit-button {
    flex-shrink: 0;
    margin-top: 16px;
    padding: 0 64px;
  }
}
</style>
